import moment from 'moment-timezone';
import { formatPrice } from 'utility/numbers';

export type ReportColumnCategory = 'ORDERS' | 'CUSTOMER' | 'TICKET DETAILS' | 'SUMMARY';
export type ReportFormat =
    | 'currency'
    | 'percent'
    | 'dateTimeLong'
    | 'dateLong'
    | 'dateShort'
    | 'dateTimeIso'
    | 'dateIso';

export type ReportColumnDefinition<TRow> = {
    id: string;
    label: string;
    category: ReportColumnCategory;
    getValue: (row: TRow) => string | number | null;
    format?: ReportFormat;
    align?: 'right';
    sortType?: 'datetime' | 'date';
    exportFormat?: ReportFormat;
    sortable?: boolean;
    summable?: boolean;
    muted?: boolean;
};

export type ReportTableConfig<TRow = Record<string, unknown>> = {
    columns: ReportColumnDefinition<TRow>[];
    defaultSelectedColumnIds: string[];
    categories: ReportColumnCategory[];
};

export function getReportColumnById<TRow>(
    columns: ReportColumnDefinition<TRow>[],
    columnId: string,
): ReportColumnDefinition<TRow> | undefined {
    return columns.find((column) => column.id === columnId);
}

const REPORT_DATETIME_FORMAT = 'DD-MMM-YY HH:mm';
const REPORT_DATE_FORMATS = {
    dateTimeLong: 'MMM D, YYYY h:mm a',
    dateLong: 'MMM D, YYYY',
    dateShort: 'MMM D',
    dateTimeIso: 'YYYY-MM-DD HH:mm',
    dateIso: 'YYYY-MM-DD',
} as const;

function isReportDateFormat(
    format: ReportFormat | undefined,
): format is keyof typeof REPORT_DATE_FORMATS {
    return format != null && format in REPORT_DATE_FORMATS;
}

export function parseReportDateTime(value: string | number | null): number {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }

    if (typeof value !== 'string' || value === '') {
        return 0;
    }

    const parsed = moment(value, REPORT_DATETIME_FORMAT, true).isValid()
        ? moment(value, REPORT_DATETIME_FORMAT, true)
        : moment(value);

    return parsed.isValid() ? parsed.valueOf() : 0;
}

function formatReportDate(value: string, format: keyof typeof REPORT_DATE_FORMATS): string {
    const parsed = moment.utc(value);
    if (!parsed.isValid()) {
        return value;
    }

    return parsed.format(REPORT_DATE_FORMATS[format]);
}

function formatReportDateTime(value: string, format: keyof typeof REPORT_DATE_FORMATS): string {
    const parsed = moment(value, REPORT_DATETIME_FORMAT, true).isValid()
        ? moment(value, REPORT_DATETIME_FORMAT, true)
        : moment(value);
    if (!parsed.isValid()) {
        return value;
    }

    return parsed.format(REPORT_DATE_FORMATS[format]);
}

function formatReportCellValue(
    def: ReportColumnDefinition<unknown>,
    value: string | number | null,
    format: ReportFormat | undefined,
): string | number {
    if (value == null || value === '') {
        return '—';
    }

    if (format === 'currency') {
        return formatPrice(Number(value));
    }

    if (format === 'percent') {
        return formatPercent(Number(value));
    }

    if (typeof value === 'string' && isReportDateFormat(format) && def.sortType === 'date') {
        return formatReportDate(value, format);
    }

    if (typeof value === 'string' && isReportDateFormat(format) && def.sortType === 'datetime') {
        return formatReportDateTime(value, format);
    }

    return value;
}

export function formatReportDisplayCellValue(
    def: ReportColumnDefinition<unknown>,
    value: string | number | null,
): string | number {
    return formatReportCellValue(def, value, def.format);
}

export function formatReportExportCellValue(
    def: ReportColumnDefinition<unknown>,
    value: string | number | null,
): string | number {
    return formatReportCellValue(def, value, def.exportFormat ?? def.format);
}

/** Ratios from SQL (0–1) shown as percentages, rounded to 2 decimals. */
export function formatPercent(value: number): string {
    if (!Number.isFinite(value)) {
        return '—';
    }

    return `${(value * 100).toFixed(2)}%`;
}

/** Footer aggregate: average for percent columns, sum for other summable columns. */
export function getReportColumnFooterAggregate(
    def: ReportColumnDefinition<unknown>,
    rows: Array<Record<string, unknown>>,
): number | null {
    if (!def.summable || rows.length === 0) {
        return null;
    }

    const values = rows
        .map((row) => {
            const value = def.getValue(row);
            return value == null ? null : Number(value);
        })
        .filter((value): value is number => value != null && Number.isFinite(value));

    if (values.length === 0) {
        return null;
    }

    const sum = values.reduce((total, value) => total + value, 0);
    return def.format === 'percent' ? sum / values.length : sum;
}
